import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Star, Award, GraduationCap, Gauge, Timer, Heart,
  Calculator, BookOpen, AlertTriangle, ChevronRight,
  Clock, Route, Zap, TrendingUp, Info
} from 'lucide-react';

const formatTime = (hours) => {
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  const s = Math.round(((hours - h) * 60 - m) * 60);
  if (h > 0) return `${h}h${m.toString().padStart(2, '0')}`;
  return `${m}min${s > 0 ? s.toString().padStart(2, '0') + 's' : ''}`;
};

const categoriesConfig = [
  {
    title: 'Amateur',
    icon: Star,
    color: '#d946ef',
    bg: 'from-fuchsia-50 to-fuchsia-100/50',
    descKey: 'catAmateurDesc',
    distances: '20 à 90 km',
    niveaux: 'Amateur 1 à 4',
    vitesse: '10 à 16 km/h'
  },
  {
    title: 'Club',
    icon: Award,
    color: '#2563eb',
    bg: 'from-blue-50 to-blue-100/50',
    descKey: 'catClubDesc',
    distances: '10 à 40 km',
    niveaux: 'Club 1 à 4',
    vitesse: '8 à 14 km/h'
  },
  {
    title: 'Préparatoire',
    icon: GraduationCap,
    color: '#16a34a',
    bg: 'from-green-50 to-green-100/50',
    descKey: 'catPrepaDesc',
    distances: '10 à 30 km',
    niveaux: 'Tous niveaux',
    vitesse: '8 à 12 km/h'
  },
  {
    title: 'Vitesse Imposée',
    icon: Gauge,
    color: '#ea580c',
    bg: 'from-orange-50 to-orange-100/50',
    descKey: 'catVIDesc',
    distances: '20 à 60 km',
    niveaux: 'Intermédiaire à confirmé',
    vitesse: '10 à 14 km/h imposés'
  },
  {
    title: 'Vitesse Régulée',
    icon: Timer,
    color: '#ca8a04',
    bg: 'from-yellow-50 to-yellow-100/50',
    descKey: 'catVRDesc',
    distances: '20 à 60 km',
    niveaux: 'Intermédiaire',
    vitesse: '10 à 16 km/h régulés'
  },
  {
    title: 'Élevage SHF',
    icon: Heart,
    color: '#dc2626',
    bg: 'from-red-50 to-red-100/50',
    descKey: 'catSHFDesc',
    distances: '20 à 40 km',
    niveaux: 'Chevaux de 4 à 6 ans',
    vitesse: '10 à 14 km/h'
  }
];

const speeds = [6, 8, 10, 12, 13, 14, 15, 16];
const distances = [10, 20, 30, 40, 60, 80];

const speedColors = {
  6: { bg: 'bg-zinc-100', text: 'text-zinc-700', header: 'bg-zinc-200' },
  8: { bg: 'bg-emerald-50', text: 'text-emerald-700', header: 'bg-emerald-100' },
  10: { bg: 'bg-blue-50', text: 'text-blue-700', header: 'bg-blue-100' },
  12: { bg: 'bg-violet-50', text: 'text-violet-700', header: 'bg-violet-100' },
  13: { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', header: 'bg-fuchsia-100' },
  14: { bg: 'bg-orange-50', text: 'text-orange-700', header: 'bg-orange-100' },
  15: { bg: 'bg-rose-50', text: 'text-rose-700', header: 'bg-rose-100' },
  16: { bg: 'bg-red-50', text: 'text-red-700', header: 'bg-red-100' },
};

export default function GuideEndurance() {
  const { t } = useTranslation();
  const [departH, setDepartH] = useState('');
  const [departM, setDepartM] = useState('');
  const [arriveeH, setArriveeH] = useState('');
  const [arriveeM, setArriveeM] = useState('');
  const [distance, setDistance] = useState('');

  const calcResult = useMemo(() => {
    const dH = parseInt(departH), dM = parseInt(departM);
    const aH = parseInt(arriveeH), aM = parseInt(arriveeM);
    const dist = parseFloat(distance);
    if (isNaN(dH) || isNaN(dM) || isNaN(aH) || isNaN(aM) || isNaN(dist) || dist <= 0) return null;

    let totalMinutes = (aH * 60 + aM) - (dH * 60 + dM);
    if (totalMinutes <= 0) totalMinutes += 24 * 60;
    const totalHours = totalMinutes / 60;
    const vitesse = dist / totalHours;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return { h, m, vitesse: vitesse.toFixed(1), totalMinutes };
  }, [departH, departM, arriveeH, arriveeM, distance]);

  const resetCalc = () => {
    setDepartH(''); setDepartM(''); setArriveeH(''); setArriveeM(''); setDistance('');
  };

  return (
    <div className="space-y-12" data-testid="guide-endurance">
      {/* ── 1. INTRO ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-8 md:p-12" data-testid="guide-intro">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-fuchsia-500/20 rounded-xl"><BookOpen className="h-6 w-6 text-fuchsia-400" /></div>
            <span className="text-sm font-medium uppercase tracking-widest text-fuchsia-400">{t('ip.guideSubtitle')}</span>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white mb-4">
            {t('ip.guideTitle')}
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed max-w-3xl">
            {t('ip.guideIntro')}
          </p>
        </div>
      </div>

      {/* ── 2. CATÉGORIES ── */}
      <div data-testid="guide-categories">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-fuchsia-100 rounded-xl"><Zap className="h-5 w-5 text-fuchsia-600" /></div>
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900">{t('ip.guideCategories')}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {categoriesConfig.map((cat) => (
            <Card key={cat.title} className={`border-0 shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br ${cat.bg}`} data-testid={`category-${cat.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 rounded-xl shadow-sm" style={{ backgroundColor: cat.color + '15' }}>
                    <cat.icon className="h-6 w-6" style={{ color: cat.color }} />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-zinc-900">{cat.title}</h4>
                  </div>
                </div>
                <p className="text-zinc-600 text-sm leading-relaxed mb-5">{t(`ip.${cat.descKey}`)}</p>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <Route className="h-4 w-4 flex-shrink-0" style={{ color: cat.color }} />
                    <span className="text-zinc-500">{t('ip.guideDistLabel')} :</span>
                    <span className="font-semibold text-zinc-800">{cat.distances}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 flex-shrink-0" style={{ color: cat.color }} />
                    <span className="text-zinc-500">{t('ip.guideNivLabel')} :</span>
                    <span className="font-semibold text-zinc-800">{cat.niveaux}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Gauge className="h-4 w-4 flex-shrink-0" style={{ color: cat.color }} />
                    <span className="text-zinc-500">{t('ip.guideSpeedLabel')} :</span>
                    <span className="font-semibold text-zinc-800">{cat.vitesse}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ── 3. TABLEAU DISTANCES / TEMPS ── */}
      <div data-testid="guide-table">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-lime-100 rounded-xl"><Clock className="h-5 w-5 text-lime-700" /></div>
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900">{t('ip.guideTable')}</h3>
        </div>
        <p className="text-zinc-500 text-sm mb-6 ml-12">{t('ip.guideTableSub')}</p>

        {/* Desktop table */}
        <div className="hidden md:block">
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="time-table-desktop">
                <thead>
                  <tr>
                    <th className="bg-zinc-800 text-white py-4 px-5 text-left font-semibold sticky left-0 z-10 min-w-[100px]">
                      <div className="flex items-center gap-2"><Route className="h-4 w-4" />{t('ip.progDistance')}</div>
                    </th>
                    {speeds.map(s => (
                      <th key={s} className={`py-3 px-3 text-center font-bold ${speedColors[s].header} ${speedColors[s].text} min-w-[90px]`}>
                        <div className="text-base">{s}</div>
                        <div className="text-xs font-normal opacity-70">km/h</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {distances.map((d, di) => (
                    <tr key={d} className={`${di % 2 === 0 ? 'bg-white' : 'bg-zinc-50/70'} hover:bg-fuchsia-50/50 transition-colors`}>
                      <td className={`py-4 px-5 font-bold text-zinc-900 sticky left-0 z-10 ${di % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} border-r border-zinc-200`}>
                        <span className="text-lg">{d}</span> <span className="text-xs text-zinc-400">km</span>
                      </td>
                      {speeds.map(s => {
                        const time = d / s;
                        return (
                          <td key={s} className={`py-4 px-3 text-center font-medium ${speedColors[s].text}`}>
                            {formatTime(time)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-4">
          {distances.map((d) => (
            <Card key={d} className="border-0 shadow-lg overflow-hidden" data-testid={`time-card-${d}km`}>
              <CardHeader className="bg-gradient-to-r from-zinc-800 to-zinc-700 py-3 px-4">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Route className="h-4 w-4" />{d} km
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-2 gap-px bg-zinc-200">
                  {speeds.map(s => {
                    const time = d / s;
                    return (
                      <div key={s} className={`p-3 ${speedColors[s].bg} flex justify-between items-center`}>
                        <span className="text-xs text-zinc-500">{s} km/h</span>
                        <span className={`font-bold ${speedColors[s].text}`}>{formatTime(time)}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ── 4. CALCULATEUR ── */}
      <div data-testid="guide-calculator">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-fuchsia-100 rounded-xl"><Calculator className="h-5 w-5 text-fuchsia-600" /></div>
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900">{t('ip.guideCalc')}</h3>
        </div>

        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Input side */}
            <div className="p-6 md:p-8 bg-gradient-to-br from-zinc-50 to-white">
              <div className="space-y-6">
                {/* Heure départ */}
                <div>
                  <label className="text-sm font-semibold text-zinc-700 mb-2 block">{t('ip.calcDepart')}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="23" placeholder="HH" value={departH} onChange={e => setDepartH(e.target.value)}
                      className="w-20 h-12 text-center text-lg font-bold rounded-xl border-2 border-zinc-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none transition-all"
                      data-testid="calc-depart-h" />
                    <span className="text-2xl font-bold text-zinc-300">:</span>
                    <input type="number" min="0" max="59" placeholder="MM" value={departM} onChange={e => setDepartM(e.target.value)}
                      className="w-20 h-12 text-center text-lg font-bold rounded-xl border-2 border-zinc-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none transition-all"
                      data-testid="calc-depart-m" />
                  </div>
                </div>
                {/* Heure arrivée */}
                <div>
                  <label className="text-sm font-semibold text-zinc-700 mb-2 block">{t('ip.calcArrivee')}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="23" placeholder="HH" value={arriveeH} onChange={e => setArriveeH(e.target.value)}
                      className="w-20 h-12 text-center text-lg font-bold rounded-xl border-2 border-zinc-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none transition-all"
                      data-testid="calc-arrivee-h" />
                    <span className="text-2xl font-bold text-zinc-300">:</span>
                    <input type="number" min="0" max="59" placeholder="MM" value={arriveeM} onChange={e => setArriveeM(e.target.value)}
                      className="w-20 h-12 text-center text-lg font-bold rounded-xl border-2 border-zinc-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none transition-all"
                      data-testid="calc-arrivee-m" />
                  </div>
                </div>
                {/* Distance */}
                <div>
                  <label className="text-sm font-semibold text-zinc-700 mb-2 block">{t('ip.calcDistance')}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" step="0.1" placeholder="Distance" value={distance} onChange={e => setDistance(e.target.value)}
                      className="w-32 h-12 text-center text-lg font-bold rounded-xl border-2 border-zinc-200 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none transition-all"
                      data-testid="calc-distance" />
                    <span className="text-sm font-medium text-zinc-400">km</span>
                  </div>
                </div>
                <Button onClick={resetCalc} variant="outline" className="rounded-full text-sm" data-testid="calc-reset">
                  {t('ip.calcReset')}
                </Button>
              </div>
            </div>

            {/* Result side */}
            <div className={`p-6 md:p-8 flex flex-col items-center justify-center transition-all duration-500 ${calcResult ? 'bg-gradient-to-br from-fuchsia-500 to-purple-600' : 'bg-gradient-to-br from-zinc-200 to-zinc-300'}`}>
              {calcResult ? (
                <div className="text-center text-white space-y-6" data-testid="calc-result">
                  <div>
                    <p className="text-sm uppercase tracking-widest opacity-80 mb-1">{t('ip.calcTotalTime')}</p>
                    <p className="text-5xl font-bold font-['Playfair_Display']">
                      {calcResult.h}h{calcResult.m.toString().padStart(2, '0')}
                    </p>
                  </div>
                  <div className="w-16 h-px bg-white/30 mx-auto" />
                  <div>
                    <p className="text-sm uppercase tracking-widest opacity-80 mb-1">{t('ip.calcAvgSpeed')}</p>
                    <p className="text-5xl font-bold font-['Playfair_Display']">
                      {calcResult.vitesse} <span className="text-2xl">km/h</span>
                    </p>
                  </div>
                  <div className="mt-4 bg-white/15 rounded-xl px-5 py-3 text-sm">
                    <span className="opacity-80">Exemple : </span>
                    <span className="font-semibold">{distance} km en {calcResult.h}h{calcResult.m.toString().padStart(2, '0')} = {calcResult.vitesse} km/h</span>
                  </div>
                </div>
              ) : (
                <div className="text-center text-zinc-500" data-testid="calc-placeholder">
                  <Calculator className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">{t('ip.calcPlaceholder')}</p>
                  <p className="text-sm opacity-70 mt-1">{t('ip.calcPlaceholderSub')}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* ── 5. À RETENIR ── */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 via-amber-50/50 to-lime-50 overflow-hidden" data-testid="guide-a-retenir">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
              <Info className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-3">{t('ip.guideRemember')}</h3>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-zinc-700">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
                  <span>{t('ip.guideRemember1')}</span>
                </li>
                <li className="flex items-start gap-2.5 text-zinc-700">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
                  <span>{t('ip.guideRemember2')}</span>
                </li>
                <li className="flex items-start gap-2.5 text-zinc-700">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
                  <span>{t('ip.guideRemember3')}</span>
                </li>
                <li className="flex items-start gap-2.5 text-zinc-700">
                  <ChevronRight className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
                  <span>{t('ip.guideRemember4')}</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
